import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Container, Grid } from '@material-ui/core';
import {
  BoxV2 as Box,
  Button,
  CampaignAttributes,
  Flex,
  Table,
  useTable,
} from 'portal-commons';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faArrowsTurnRight,
  faCalendarRange,
  faCircleUser,
  faMessageDots,
  faRotateRight,
  faSquareExclamation,
  faSquareMinus,
  faSquarePollHorizontal,
  faTag,
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';

import { Loader } from '@/shared_elements';
import {
  getCampaignDetailApi,
  getCampaignEventsListApi,
  getCampaignSharingChain,
  updateCampaignStatusApi,
} from '../../apiServices';
import {
  CampaignActionModal,
  CampaignBrandDetails,
  CampaignCarrierStatus,
  CampaignCspDetails,
  CampaignDetailsBasic,
  CampaignResellerDetails,
  EventsListingRow,
  MultimediaList,
  NumbersProvisioned,
  SampleMessageView,
  SharingChain,
} from '../../components';
import '../../assets/campaign-details-module.scss';
import { toastFlashMessage } from '@/utils';
import { ATTACHMENT_FOLDER } from '../../constants';
import { Campaign } from '../../types';
import { MAX_EVENT_RECORDS } from '../../../Events/constants';
import { CampaignSharingChain } from '@/apis/core';
import { ModalActionType } from '../../components/CampaignActionModal/CampaignActionModal';
import { EventData } from '@/applications/MnoPortal/Events/types';
import { MnoStatus } from '@/constants/enum';

interface SectionTitleProps {
  children: ReactNode;
  icon: IconDefinition;
  className?: string;
  iconSize?: FontAwesomeIconProps['size'];
}

interface LabelProps {
  children: ReactNode;
  className?: string;
}

interface ValueProps {
  children: ReactNode;
  className?: string;
}

const headRows = [
  { label: 'EVENT TYPE', id: 'eventType', sortable: false },
  { label: 'CATEGORY', id: 'categoryType', sortable: false },
  { label: 'CAMPAIGN ID', id: 'campaignUid', sortable: false },
  { label: 'BRAND ID', id: 'brandUid', sortable: false },
  { label: 'DATE & TIME', id: 'updateDate', sortable: false },
  { label: 'USER', id: 'authenticatedUser', sortable: false },
  { label: 'DESCRIPTION', id: 'description', sortable: false },
];

const SectionTitle: FC<SectionTitleProps> = ({
  children,
  icon,
  className,
  iconSize,
}) => (
  <div className={className || 'details-heading'}>
    <Flex sx={{ flexDirection: 'row', alignItems: 'center' }}>
      <Box sx={{ mr: 'xs' }}>
        <FontAwesomeIcon
          size={iconSize}
          icon={icon}
          style={{ fontSize: '18px' }}
        />
      </Box>
      <h3 className="heading2">{children}</h3>
    </Flex>
  </div>
);

const Label: FC<LabelProps> = ({ children, className }) => (
  <Box
    className={className}
    sx={{
      width: '220px',
      fontSize: '14px',
    }}
  >
    {children}
  </Box>
);

const Value: FC<ValueProps> = ({ children, className }) => (
  <Box
    className={className}
    sx={{
      flex: 1,
      paddingRight: '4px',
      fontWeight: 600,
      fontSize: '14px',
    }}
  >
    {children}
  </Box>
);

const EVENTS_PAGE_SIZE = 10;

type ModalType = ModalActionType | '';

const CampaignDetailsUS = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation<{ campaign: Campaign; goBackPage: string }>();
  const history = useHistory();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loader, setLoader] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [goBackPage, setGoBackPage] = useState('/campaigns');
  const [sharingNodes, setSharingNodes] =
    useState<CampaignSharingChain['nodes']>();
  const [statusLoader, setStatusLoader] = useState(false);
  const [modalType, setModalType] = useState<ModalType>('');
  const searchParams = new URLSearchParams(location.search);
  const eventsTable = useTable<EventData>(
    async (filter) => {
      const response = await getCampaignEventsListApi({
        ...filter,
        campaignUid: id,
      });
      return {
        data: response?.records || [],
        page: response?.page || 1,
        rowsPerPage: response?.recordsPerPage || EVENTS_PAGE_SIZE,
        total: response?.totalRecords || 0,
      };
    },
    {
      deps: [id, location?.search],
      cappingTotal: MAX_EVENT_RECORDS,
      defaultFilter: {
        ascendingOrder: false,
        sortField: 'createDate',
        page: Number(searchParams.get('page')) || 1,
      },
    }
  );

  const fetchCampaignDetail = async (campaignId: string) => {
    try {
      const data = await getCampaignDetailApi(campaignId);
      setCampaign(data ?? null);
    } catch (e) {
      // Not handling the error, letting it fail silently
    } finally {
      setLoader(false);
    }
  };

  const fetchSharingChain = async (campaignUid: string) => {
    const response: CampaignSharingChain = await getCampaignSharingChain(
      campaignUid
    );
    setSharingNodes(response?.nodes);
  };

  useEffect(() => {
    if (location.state) {
      const { campaign: stateCampaign, goBackPage: backPage } = location.state;
      if (!stateCampaign) {
        fetchCampaignDetail(id);
      } else {
        setCampaign(stateCampaign);
        setLoader(false);
      }
      if (backPage) setGoBackPage(backPage);
    } else {
      fetchCampaignDetail(id);
    }
    fetchSharingChain(id);
  }, [id, location.state]);

  const patchCampaignOperationStatus = (
    status: Campaign['operationStatus']
  ) => {
    setCampaign((prevCampaign) =>
      prevCampaign
        ? {
            ...prevCampaign,
            operationStatus: status,
          }
        : null
    );
  };

  const toggleModal = (flag: boolean, type: ModalType = '') => {
    setModalOpen(flag);
    setModalType(type);
  };

  const updateOperationStatus = (type: ModalType) => {
    fetchCampaignDetail(id);
    patchCampaignOperationStatus(
      type === 'suspend' ? MnoStatus.Suspended : MnoStatus.Registered
    );
  };

  const handleGoBack = () => {
    history.push(`${goBackPage}`);
  };

  const handleUpdateCampaignStatus = async (status: MnoStatus) => {
    if (!campaign) {
      return;
    }
    setStatusLoader(true);
    try {
      const response = await updateCampaignStatusApi(campaign.uid, {
        status,
        explanation: status === MnoStatus.Registered ? 'Campaign approved' : '',
      });
      setStatusLoader(false);
      if (response?.ok) {
        toastFlashMessage(
          `Campaign ${
            status === MnoStatus.Registered ? 'approved' : ''
          } successfully`,
          'success'
        );
        patchCampaignOperationStatus(status);
      }
    } catch (error) {
      setStatusLoader(false);
    }
  };

  const withDefaultValue = (
    value: string | null | undefined,
    defaultVal = 'N/A'
  ) => {
    return value ?? defaultVal;
  };

  let carrier = null;
  if (campaign && typeof campaign.rulesEngineResultsJson === 'string') {
    try {
      carrier = JSON.parse(campaign.rulesEngineResultsJson).mno;
    } catch (e) {
      // Defensively parsing JSON
    }
  }

  const showFileAComplaintButton =
    campaign && campaign.operationStatus !== MnoStatus.Review;

  const canRegister =
    campaign?.allowedNewStatus?.includes(MnoStatus.Registered) ??
    [MnoStatus.Review, MnoStatus.Rejected, MnoStatus.Suspended].includes(
      campaign?.operationStatus as MnoStatus
    );

  const showApproveButton =
    canRegister && campaign?.operationStatus !== MnoStatus.Suspended;

  const showLiftSuspensionButton =
    canRegister && campaign?.operationStatus === MnoStatus.Suspended;

  const showRejectButton =
    campaign?.allowedNewStatus?.includes(MnoStatus.Rejected) ??
    campaign?.operationStatus === MnoStatus.Review;

  const showSuspendButton =
    campaign?.allowedNewStatus?.includes(MnoStatus.Suspended) ??
    campaign?.operationStatus === MnoStatus.Registered;

  return (
    <section
      className="campaign-details-section page-content"
      data-testid="campaignDetail"
    >
      {loader ? (
        <Loader />
      ) : campaign ? (
        <>
          <Container maxWidth={false} className="campaign-details-container">
            <Grid container className="top-blk">
              <Grid
                item
                xs={6}
                className="back-btn"
                data-testid="campaignDetailBackButton"
              >
                <a onClick={handleGoBack} className="paragraph">
                  <Flex sx={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Box sx={{ mr: 's' }}>
                      <FontAwesomeIcon icon={faAngleLeft} size="lg" />
                    </Box>
                    {goBackPage === '/campaigns'
                      ? 'back to all campaigns'
                      : 'back'}
                  </Flex>
                </a>
              </Grid>
              <Grid item xs={6} className="button-wrapper">
                <ul
                  className="list-inline"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'end',
                  }}
                >
                  {showFileAComplaintButton && (
                    <li>
                      <Button
                        data-testid="fileAComplaintButton"
                        portal="mno"
                        shape="rounded"
                        variant="outline"
                        color="primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          toggleModal(true, 'report');
                        }}
                      >
                        <FontAwesomeIcon icon={faSquareExclamation} size="lg" />
                        <span>File a Complaint</span>
                      </Button>
                    </li>
                  )}
                  {showApproveButton && (
                    <li>
                      <Button
                        data-testid="approveCampaignButton"
                        portal="mno"
                        disabled={statusLoader}
                        shape="rounded"
                        variant="standard"
                        color="secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          handleUpdateCampaignStatus(MnoStatus.Registered);
                        }}
                      >
                        <Box>
                          <FontAwesomeIcon icon={faCheck} />
                        </Box>
                        <span>Approve Campaign</span>
                      </Button>
                    </li>
                  )}
                  {showRejectButton && (
                    <li>
                      <Button
                        data-testid="rejectCampaignButton"
                        portal="mno"
                        disabled={statusLoader}
                        shape="rounded"
                        variant="standard"
                        color="primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          toggleModal(true, 'reject');
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                        <span>Reject Campaign</span>
                      </Button>
                    </li>
                  )}
                  {showSuspendButton && (
                    <li>
                      <Button
                        data-testid="suspendCampaignButton"
                        portal="mno"
                        shape="rounded"
                        variant="standard"
                        color="primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          toggleModal(true, 'suspend');
                        }}
                      >
                        <Box>
                          <FontAwesomeIcon icon={faSquareMinus} size="lg" />
                        </Box>
                        <span>Suspend Campaign</span>
                      </Button>
                    </li>
                  )}
                  {showLiftSuspensionButton && (
                    <li>
                      <Button
                        data-testid="liftSuspensionButton"
                        portal="mno"
                        shape="rounded"
                        variant="standard"
                        color="secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          toggleModal(true, 'lift-suspension');
                        }}
                      >
                        <FontAwesomeIcon icon={faRotateRight} />
                        <span>Lift Suspension</span>
                      </Button>
                    </li>
                  )}
                </ul>
              </Grid>
            </Grid>
            <Grid container className="campaign-details basic">
              <Grid item xs={12} data-testid="campaignDetailUid">
                <div className="title">
                  <h3 className="heading1">
                    10DLC Campaign ID: {campaign.uid}
                  </h3>
                </div>
              </Grid>
              <Grid item xs={12}>
                <CampaignDetailsBasic campaign={campaign} />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <CampaignCarrierStatus campaign={campaign} />
              </Grid>
              <Grid item xs={12}>
                <SharingChain carrier={carrier} nodes={sharingNodes || []} />
              </Grid>
              <Grid item xs={12}>
                <NumbersProvisioned campaignUid={campaign.uid} />
              </Grid>
              <Grid item xs={12}>
                <div className="csp-brand-reseller details">
                  <SectionTitle icon={faCircleUser}>CSP Details</SectionTitle>
                  <CampaignCspDetails cspInfo={campaign?.brand?.csp || {}} />
                  <SectionTitle
                    icon={faTag}
                    iconSize="lg"
                    className="details-heading brand"
                  >
                    Brand Details
                  </SectionTitle>
                  <CampaignBrandDetails brandInfo={campaign?.brand} />
                  <SectionTitle
                    icon={faArrowsTurnRight}
                    iconSize="lg"
                    className="details-heading reseller"
                  >
                    Reseller Details
                  </SectionTitle>
                  <CampaignResellerDetails
                    resellerInfo={campaign?.reseller || {}}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="details">
                  <SectionTitle icon={faSquarePollHorizontal}>
                    Campaign Details
                  </SectionTitle>
                  <Grid container style={{ marginBottom: '12px' }}>
                    <Grid
                      xs={7}
                      container
                      direction="row"
                      alignItems="flex-start"
                      data-testid="campaignDetailsDescription"
                    >
                      <Label className="paragraph">Campaign Description:</Label>
                      <Value className="paragraph">
                        {withDefaultValue(campaign.description)}
                      </Value>
                    </Grid>
                    <Grid
                      xs={5}
                      container
                      direction="row"
                      alignItems="flex-start"
                      data-testid="campaignDetailsPrivacyPolicyLink"
                    >
                      <Label className="paragraph">Privacy Policy Link:</Label>
                      <Value className="paragraph">
                        {withDefaultValue(campaign.privacyPolicyLink)}
                      </Value>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid
                      xs={7}
                      container
                      direction="row"
                      alignItems="flex-start"
                      data-testid="campaignDetailsMessageFlow"
                    >
                      <Label className="paragraph">
                        Call-to-Action / Message Flow:
                      </Label>
                      <Value className="paragraph">
                        {withDefaultValue(campaign.messageFlow)}
                      </Value>
                    </Grid>
                    <Grid
                      xs={5}
                      container
                      direction="row"
                      alignItems="flex-start"
                      data-testid="campaignDetailsTocLink"
                    >
                      <Label className="paragraph">
                        Terms and Conditions Link:
                      </Label>
                      <Value className="paragraph">
                        {withDefaultValue(campaign.termsAndConditionsLink)}
                      </Value>
                    </Grid>
                  </Grid>
                </div>
              </Grid>
              <Grid item xs={12}>
                <MultimediaList
                  sectionTitle="CTA (Call-to-Action), Privacy Policy and/or Terms and Conditions
            Multimedia Upload"
                  subText="Supporting information for opt in, call-to-action, terms and conditions, privacy policy, etc. Not intended for MMS sample messages."
                  listTitle="Multimedia Files"
                  campaignId={campaign.uid}
                  data-testid="CtaMultimediaList"
                  folder={ATTACHMENT_FOLDER.SUPPORTING_DOC}
                />
              </Grid>
              <Grid item xs={12}>
                <div
                  className="sample-messages details"
                  data-testid="campaignDetailsSampleMessages"
                >
                  <SectionTitle icon={faMessageDots} iconSize="lg">
                    Sample Messages
                  </SectionTitle>
                  {(
                    [
                      'sample1',
                      'sample2',
                      'sample3',
                      'sample4',
                      'sample5',
                    ] as const
                  ).map((key) =>
                    campaign[key] ? (
                      <SampleMessageView
                        key={key}
                        index={key.slice(-1)}
                        message={campaign[key] as string}
                      />
                    ) : null
                  )}
                </div>
              </Grid>
              <Grid item xs={12}>
                <MultimediaList
                  sectionTitle="Sample Multimedia"
                  listTitle="Sample Multimedia Files"
                  data-testid="campaignSampleMultimedia"
                  campaignId={campaign.uid}
                  folder={ATTACHMENT_FOLDER.MMS}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  className="attributes details"
                  data-testid="campaignDetailCampaignAndContentAttributes"
                >
                  <SectionTitle icon={faSquarePollHorizontal}>
                    Campaign and Content Attributes
                  </SectionTitle>
                  <CampaignAttributes.ReadonlyPanel
                    data={{
                      ...campaign,
                      optinKeywords: campaign.optinKeywords?.split(',') || [],
                      optoutKeywords: campaign.optoutKeywords?.split(',') || [],
                      helpKeywords: campaign.helpKeywords?.split(',') || [],
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <div className="campaign-events details">
                  <SectionTitle icon={faCalendarRange}>Events</SectionTitle>
                  <Grid container>
                    <Table
                      disableHover
                      headRows={headRows}
                      emptyState="no events to view"
                      loading={eventsTable.loading}
                      tableData={eventsTable.tableData.map((record, index) => (
                        <EventsListingRow key={index} event={record} />
                      ))}
                      handleChangePage={eventsTable.handleChangePage}
                      createSortHandler={eventsTable.handleSorting}
                      pagination={eventsTable.pagination}
                    />
                  </Grid>
                </div>
              </Grid>
            </Grid>
          </Container>
          <CampaignActionModal
            open={modalOpen}
            handleClose={() => toggleModal(false)}
            handleOperationStatus={updateOperationStatus}
            type={modalType as ModalActionType}
            campaign={campaign}
          />
        </>
      ) : (
        <div className="campaign-details-container">Campaign not found</div>
      )}
    </section>
  );
};

export default CampaignDetailsUS;
